import type { ConnectGrant, ConnectGrantRequest, ConnectGrantType } from "@ando/core";
import {
  Badge,
  Button,
  ConnectionPicker,
  type ConnectionPickerOption,
  DateTimePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  toast,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Check,
  Database,
  Eye,
  FileText,
  Loader2,
  Lock,
  Plug,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import * as React from "react";

import { useCanvases } from "@/features/canvases";
import { useDocuments } from "@/features/workspace/hooks/useDocuments.js";
import { useAddConnectGrant, useRevokeConnectGrant } from "../hooks/useConnectMutations.js";
import { useGrantableConnections } from "../hooks/useConnectSpaces.js";
import { GuestPreviewDialog } from "./GuestPreviewDialog.js";
import {
  ConfirmDialog,
  GRANT_TYPE_ICON,
  RailSectionHeader,
  TokenInput,
  formatRelative,
} from "./connectShared.js";

/**
 * GrantManager — owner-side authoring of the data boundary (Risorse tab).
 *
 * Drops into the ``connect.$spaceId`` route's "Risorse condivise" extension
 * point. Every active grant renders as a trust row: type glyph + resource
 * label + a READ-ONLY badge + relative expiry + a spatially-separated,
 * danger-toned revoke (confirm dialog + UNDO toast). "Aggiungi risorsa" opens
 * a progressive-disclosure authoring dialog (type chooser → per-type form):
 *
 *   - KNOWLEDGE  → pick a Knowledge Canvas OR a set of documents, with a live
 *                  "this is the ONLY knowledge the guest can query" preview.
 *   - CONNECTOR  → CONNECTION-BASED, ACCOUNT-PICKER-FIRST: pick a structured-data
 *                  connection (from ``useGrantableConnections``), then an
 *                  optional SQL table allowlist. The BE forces ``read_only``
 *                  server-side.
 *   - DOCUMENT   → pick a single document.
 *
 * DRY: reuses ``<ConnectionPicker>`` (the SAME account picker the action forms
 * and workspace surfaces mount), ``<DateTimePicker>`` (the one app-wide date
 * control), ``useCanvases`` / ``useDocuments`` (existing list hooks), and the
 * pre-wired ``useAddConnectGrant`` / ``useRevokeConnectGrant`` mutations.
 */

// ---------------------------------------------------------------------------
// Connect grants are read-only by contract. The connector grant is
// CONNECTION-BASED: the set of OFFERABLE structured-data connections is owned by
// the BE (a fail-closed grantable set) and read at runtime via
// ``useGrantableConnections`` — the form NEVER hard-codes options, so they can't
// drift from what the BE accepts. ``connection_id`` is the exact id the BE
// accepts on submit; ``label`` is white-label; ``provider`` feeds the glyph.
// ---------------------------------------------------------------------------

type AddStage = "type" | "knowledge" | "connector" | "document";

export interface GrantManagerProps {
  readonly spaceId: string;
  readonly spaceName: string;
  readonly grants: readonly ConnectGrant[];
}

// ===========================================================================
// Root
// ===========================================================================

export function GrantManager({ spaceId, spaceName, grants }: GrantManagerProps) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const [addOpen, setAddOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [pendingRevoke, setPendingRevoke] = React.useState<ConnectGrant | null>(null);

  const addGrant = useAddConnectGrant();
  const revokeGrant = useRevokeConnectGrant();

  const handleRevoke = React.useCallback(
    (grant: ConnectGrant) => {
      revokeGrant.mutate(
        { spaceId, grantId: grant.id },
        {
          onSuccess: () => {
            setPendingRevoke(null);
            toast.success(i18n._(msg`Risorsa rimossa`), {
              action: {
                label: i18n._(msg`Annulla`),
                onClick: () => {
                  // UNDO = replay the authoring mutation with the same scope.
                  const req = grantToRequest(grant);
                  addGrant.mutate(
                    { spaceId, grant: req },
                    {
                      onError: () => toast.error(i18n._(msg`Impossibile ripristinare la risorsa`)),
                    },
                  );
                },
              },
            });
          },
          onError: () => toast.error(i18n._(msg`Impossibile rimuovere la risorsa`)),
        },
      );
    },
    [revokeGrant, addGrant, spaceId, i18n],
  );

  return (
    <div className="flex flex-col gap-2 py-2">
      <RailSectionHeader
        title={<Trans>Fonti condivise</Trans>}
        action={
          grants.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2"
              onClick={() => setPreviewOpen(true)}
            >
              <Eye size={13} strokeWidth={2} aria-hidden />
              <Trans>Anteprima ospite</Trans>
            </Button>
          ) : null
        }
      />

      {grants.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {grants.map((g) => (
            <GrantRow key={g.id} grant={g} locale={locale} onRevoke={() => setPendingRevoke(g)} />
          ))}
        </ul>
      ) : null}

      <Button
        variant="secondary"
        size="md"
        className="mt-1 w-full justify-center gap-1.5"
        onClick={() => setAddOpen(true)}
      >
        <Plus size={14} strokeWidth={2.2} aria-hidden />
        <Trans>Aggiungi risorsa</Trans>
      </Button>

      <AddGrantDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        spaceId={spaceId}
        addGrant={addGrant}
      />

      <GuestPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        spaceId={spaceId}
        spaceName={spaceName}
        grants={grants}
      />

      <ConfirmDialog
        open={pendingRevoke !== null}
        onOpenChange={(o) => {
          if (!o) setPendingRevoke(null);
        }}
        title={<Trans>Rimuovere questa risorsa?</Trans>}
        description={
          <Trans>
            Gli ospiti non potranno più interrogare questa fonte. Puoi annullare subito dopo.
          </Trans>
        }
        confirmLabel={<Trans>Rimuovi</Trans>}
        cancelLabel={<Trans>Annulla</Trans>}
        pending={revokeGrant.isPending}
        onConfirm={() => {
          if (pendingRevoke) handleRevoke(pendingRevoke);
        }}
      />
    </div>
  );
}
GrantManager.displayName = "GrantManager";

// ===========================================================================
// Grant row — the trust row.
// ===========================================================================

function GrantRow({
  grant,
  locale,
  onRevoke,
}: {
  readonly grant: ConnectGrant;
  readonly locale: string;
  readonly onRevoke: () => void;
}) {
  const { i18n } = useLingui();
  const Icon = GRANT_TYPE_ICON[grant.grant_type];
  const expiryIso = typeof grant.config?.expires_at === "string" ? grant.config.expires_at : null;
  const expiry = formatRelative(expiryIso, locale);

  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-hairline/70 bg-white/55 px-3 py-2.5">
      <span
        aria-hidden
        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cream-deep text-ink-1"
      >
        <Icon size={14} strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-[12.5px] text-ink-1">{grantLabel(grant, i18n)}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge
            variant="secondary"
            className="gap-1 py-0 text-[10px]"
            aria-label={i18n._(msg`Sola lettura`)}
          >
            <ShieldCheck size={10} strokeWidth={2.2} aria-hidden />
            <Trans>Sola lettura</Trans>
          </Badge>
          {expiry ? (
            <span className="text-[10.5px] text-ink-2">
              <Trans>Scade</Trans> {expiry}
            </span>
          ) : (
            <span className="text-[10.5px] text-ink-2">
              <Trans>Senza scadenza</Trans>
            </span>
          )}
        </div>
      </div>
      {/* Destructive, spatially separated from any normal action. */}
      <button
        type="button"
        onClick={onRevoke}
        aria-label={i18n._(msg`Rimuovi risorsa`)}
        className={cn(
          "shrink-0 rounded-md p-1.5 text-ink-3 transition-colors",
          "hover:bg-rose-50 hover:text-rose-600",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/45",
        )}
      >
        <Trash2 size={14} strokeWidth={2} aria-hidden />
      </button>
    </li>
  );
}

// ===========================================================================
// Add-grant dialog — progressive disclosure.
// ===========================================================================

function AddGrantDialog({
  open,
  onOpenChange,
  spaceId,
  addGrant,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly spaceId: string;
  readonly addGrant: ReturnType<typeof useAddConnectGrant>;
}) {
  const { i18n } = useLingui();
  const [stage, setStage] = React.useState<AddStage>("type");

  React.useEffect(() => {
    if (open) setStage("type");
  }, [open]);

  const submit = React.useCallback(
    (grant: ConnectGrantRequest) => {
      addGrant.mutate(
        { spaceId, grant },
        {
          onSuccess: () => {
            onOpenChange(false);
            toast.success(i18n._(msg`Risorsa condivisa`));
          },
          onError: () => toast.error(i18n._(msg`Impossibile condividere la risorsa`)),
        },
      );
    },
    [addGrant, spaceId, onOpenChange, i18n],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Plus size={15} strokeWidth={2} aria-hidden /> <Trans>Aggiungi risorsa</Trans>
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            <Trans>
              Scegli cosa condividere. Gli ospiti potranno interrogare solo le fonti che aggiungi
              qui, sempre in sola lettura.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        {stage === "type" ? (
          <TypeChooser onChoose={setStage} />
        ) : stage === "knowledge" ? (
          <KnowledgeForm
            pending={addGrant.isPending}
            onBack={() => setStage("type")}
            onSubmit={submit}
          />
        ) : stage === "connector" ? (
          <ConnectorForm
            pending={addGrant.isPending}
            onBack={() => setStage("type")}
            onSubmit={submit}
          />
        ) : (
          <DocumentForm
            pending={addGrant.isPending}
            onBack={() => setStage("type")}
            onSubmit={submit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TypeChooser({ onChoose }: { readonly onChoose: (s: AddStage) => void }) {
  const options: ReadonlyArray<{
    readonly stage: AddStage;
    readonly icon: typeof Database;
    readonly title: React.ReactNode;
    readonly body: React.ReactNode;
  }> = [
    {
      stage: "knowledge",
      icon: Database,
      title: <Trans>Base di conoscenza</Trans>,
      body: <Trans>Un Canvas o un insieme di documenti come unica conoscenza interrogabile.</Trans>,
    },
    {
      stage: "connector",
      icon: Plug,
      title: <Trans>Fonte connessa</Trans>,
      body: (
        <Trans>
          Un connettore a dati strutturati collegato, in sola lettura, limitabile alle tabelle
          scelte.
        </Trans>
      ),
    },
    {
      stage: "document",
      icon: FileText,
      title: <Trans>Documento</Trans>,
      body: <Trans>Un singolo file dei tuoi documenti.</Trans>,
    },
  ];
  return (
    <div className="grid gap-2 py-2">
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button
            key={o.stage}
            type="button"
            onClick={() => onChoose(o.stage)}
            className={cn(
              "flex items-start gap-3 rounded-lg border border-hairline/70 bg-white/55 px-3.5 py-3 text-left transition-all",
              "hover:-translate-y-px hover:border-ando-orange/40 hover:bg-white/80",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            <span
              aria-hidden
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cream-deep text-ink-1"
            >
              <Icon size={16} strokeWidth={1.9} />
            </span>
            <span className="min-w-0">
              <span className="block font-medium text-[13px] text-ink-1">{o.title}</span>
              <span className="mt-0.5 block text-[11.5px] text-ink-2">{o.body}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form footer — shared across the three per-type forms.
// ---------------------------------------------------------------------------

function FormFooter({
  onBack,
  onSubmit,
  pending,
  disabled,
}: {
  readonly onBack: () => void;
  readonly onSubmit: () => void;
  readonly pending: boolean;
  readonly disabled: boolean;
}) {
  return (
    <DialogFooter className="gap-2">
      <Button variant="ghost" size="md" onClick={onBack} disabled={pending}>
        <Trans>Indietro</Trans>
      </Button>
      <Button
        variant="primary"
        size="md"
        className="gap-1.5"
        onClick={onSubmit}
        disabled={disabled || pending}
      >
        {pending ? <Loader2 className="animate-spin" size={14} aria-hidden /> : null}
        <Trans>Condividi</Trans>
      </Button>
    </DialogFooter>
  );
}

function FieldLabel({
  children,
  required,
  htmlFor,
}: {
  readonly children: React.ReactNode;
  readonly required?: boolean;
  readonly htmlFor?: string;
}) {
  return (
    <label htmlFor={htmlFor} className="flex items-center gap-1 font-medium text-[12px] text-ink-1">
      {children}
      {required ? (
        <span className="text-rose-500" aria-hidden>
          *
        </span>
      ) : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// KNOWLEDGE form
// ---------------------------------------------------------------------------

function KnowledgeForm({
  onBack,
  onSubmit,
  pending,
}: {
  readonly onBack: () => void;
  readonly onSubmit: (g: ConnectGrantRequest) => void;
  readonly pending: boolean;
}) {
  const { i18n } = useLingui();
  const [source, setSource] = React.useState<"canvas" | "documents">("canvas");
  const [canvasId, setCanvasId] = React.useState<string | null>(null);
  const [docIds, setDocIds] = React.useState<readonly string[]>([]);
  const [expiry, setExpiry] = React.useState<Date | null>(null);

  const { canvases, isLoading: canvasesLoading } = useCanvases();
  const { documents, isLoading: docsLoading } = useDocuments({ limit: 100 });

  const selectedCanvas = canvases.find((c) => c.id === canvasId) ?? null;
  const selectedDocs = documents.filter((d) => docIds.includes(d.doc_id));

  const valid = source === "canvas" ? canvasId !== null : docIds.length > 0;

  const handleSubmit = () => {
    const config: Record<string, unknown> = {};
    if (expiry) config.expires_at = expiry.toISOString();
    const base: ConnectGrantRequest = {
      grant_type: "knowledge",
      config: Object.keys(config).length > 0 ? config : null,
    };
    if (source === "canvas" && canvasId) {
      onSubmit({ ...base, canvas_id: canvasId });
    } else {
      onSubmit({ ...base, content_item_ids: [...docIds] });
    }
  };

  return (
    <div className="flex flex-col gap-3 py-2">
      {/* Source toggle */}
      <div className="inline-flex items-center gap-1 self-start rounded-lg border border-black/5 bg-white/45 p-1">
        {(
          [
            { id: "canvas", label: i18n._(msg`Canvas`) },
            { id: "documents", label: i18n._(msg`Documenti`) },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSource(t.id)}
            className={
              source === t.id
                ? "rounded-md bg-white px-3 py-1.5 font-medium text-[11px] text-ink-1 shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                : "rounded-md px-3 py-1.5 font-medium text-[11px] text-ink-3 hover:text-ink-1"
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {source === "canvas" ? (
        <div className="flex flex-col gap-1.5">
          <FieldLabel required>
            <Trans>Base di conoscenza</Trans>
          </FieldLabel>
          <PickList
            loading={canvasesLoading}
            emptyLabel={i18n._(msg`Nessun Canvas disponibile`)}
            items={canvases.map((c) => ({
              id: c.id,
              label: c.title,
              secondary:
                c.source_count === 1 ? i18n._(msg`1 fonte`) : i18n._(msg`${c.source_count} fonti`),
            }))}
            selected={canvasId ? [canvasId] : []}
            multi={false}
            onToggle={(id) => setCanvasId((cur) => (cur === id ? null : id))}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <FieldLabel required>
            <Trans>Documenti</Trans>
          </FieldLabel>
          <DocumentPicker
            loading={docsLoading}
            documents={documents}
            selected={docIds}
            multi
            onToggle={(id) =>
              setDocIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
            }
          />
        </div>
      )}

      {/* Preview — the "ONLY knowledge" trust note. */}
      {(selectedCanvas || selectedDocs.length > 0) && (
        <div className="rounded-lg border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-2.5">
          <p className="flex items-center gap-1.5 font-medium text-[11.5px] text-ink-1">
            <Lock size={12} strokeWidth={2.2} aria-hidden />
            <Trans>Questa è l'UNICA conoscenza che l'ospite potrà interrogare.</Trans>
          </p>
          <ul className="mt-1.5 flex flex-col gap-0.5 text-[11px] text-ink-2">
            {selectedCanvas ? (
              <li className="truncate">• {selectedCanvas.title}</li>
            ) : (
              selectedDocs.slice(0, 6).map((d) => (
                <li key={d.doc_id} className="truncate">
                  • {d.title || d.filename}
                </li>
              ))
            )}
            {selectedDocs.length > 6 ? (
              <li className="text-ink-2">
                <Trans>+{selectedDocs.length - 6} altri</Trans>
              </li>
            ) : null}
          </ul>
        </div>
      )}

      {/* Expiry — progressive (optional). */}
      <div className="flex flex-col gap-1.5">
        <FieldLabel>
          <Trans>Scadenza (opzionale)</Trans>
        </FieldLabel>
        <DateTimePicker
          value={expiry}
          onChange={setExpiry}
          withTime
          minDate={new Date()}
          placeholder={i18n._(msg`Senza scadenza`)}
          ariaLabel={i18n._(msg`Scadenza della condivisione`)}
        />
        <p className="text-[11px] text-ink-2">
          <Trans>Dopo questa data l'accesso a questa risorsa si chiude da solo.</Trans>
        </p>
      </div>

      <FormFooter onBack={onBack} onSubmit={handleSubmit} pending={pending} disabled={!valid} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CONNECTOR form — ACCOUNT-PICKER-FIRST.
// ---------------------------------------------------------------------------

function ConnectorForm({
  onBack,
  onSubmit,
  pending,
}: {
  readonly onBack: () => void;
  readonly onSubmit: (g: ConnectGrantRequest) => void;
  readonly pending: boolean;
}) {
  const { i18n } = useLingui();
  // CONNECTION-BASED + ACCOUNT-PICKER-FIRST: the structured-data connection is
  // the FIRST decision. ``connectionId`` is the exact id the BE accepts.
  const [connectionId, setConnectionId] = React.useState<string | null>(null);
  const [allowlist, setAllowlist] = React.useState<readonly string[]>([]);
  const [expiry, setExpiry] = React.useState<Date | null>(null);

  // Offerable connections come from the BE grantable set — never hard-coded —
  // so the picker can't list a connection the BE would reject.
  const {
    connections: grantable,
    isLoading: connsLoading,
    isError: connsError,
  } = useGrantableConnections();

  const pickerOptions: readonly ConnectionPickerOption[] = grantable.map((c) => ({
    id: c.connection_id,
    provider: c.provider,
    account_label: c.label,
  }));

  const valid = connectionId !== null;

  const handleSubmit = () => {
    if (!connectionId) return;
    // The BE forces ``read_only`` server-side; the optional table allowlist
    // rides in ``config`` (empty = the whole connection, read-only).
    const config: Record<string, unknown> = {};
    if (allowlist.length > 0) config.table_allowlist = [...allowlist];
    if (expiry) config.expires_at = expiry.toISOString();
    onSubmit({
      grant_type: "connector",
      connection_id: connectionId,
      config: Object.keys(config).length > 0 ? config : null,
    });
  };

  return (
    <div className="flex flex-col gap-3 py-2">
      {/* Read-only invariant — prominent up front. */}
      <div className="flex items-center gap-2 rounded-lg border border-hairline/70 bg-cream-deep/50 px-3 py-2">
        <Badge
          variant="secondary"
          className="gap-1 py-0 text-[10px]"
          aria-label={i18n._(msg`Sola lettura`)}
        >
          <ShieldCheck size={10} strokeWidth={2.2} aria-hidden />
          <Trans>Sola lettura</Trans>
        </Badge>
        <p className="text-[11.5px] text-ink-2">
          <Trans>L'ospite può interrogare questa fonte, ma non può modificare nulla.</Trans>
        </p>
      </div>

      {/* 1 — connection (ACCOUNT-PICKER-FIRST). Options from the BE grantable set. */}
      <div className="flex flex-col gap-1.5">
        <FieldLabel required>
          <Trans>Connettore a dati strutturati</Trans>
        </FieldLabel>
        {connsLoading ? (
          <span className="flex items-center gap-1.5 rounded-md border border-black/10 border-dashed bg-white/35 px-3 py-2 text-[11.5px] text-ink-2">
            <Loader2 size={13} aria-hidden className="animate-spin text-ink-3" />
            <Trans>Carico i connettori…</Trans>
          </span>
        ) : connsError ? (
          <p className="text-[11px] text-rose-600">
            <Trans>Impossibile caricare i connettori condivisibili. Riprova.</Trans>
          </p>
        ) : pickerOptions.length === 0 ? (
          <span className="rounded-md border border-black/10 border-dashed bg-white/35 px-3 py-2 text-[11.5px] text-ink-2">
            <Trans>Nessun connettore a dati strutturati collegato.</Trans>
          </span>
        ) : (
          <ConnectionPicker
            connections={pickerOptions}
            activeIds={connectionId ? [connectionId] : []}
            onChange={(ids) => setConnectionId(ids.length > 0 ? ids[ids.length - 1]! : null)}
            label={i18n._(msg`Connettore`)}
            allLabel={i18n._(msg`Tutti`)}
            noneLabel={i18n._(msg`Nessuno`)}
          />
        )}
      </div>

      {/* 2 — table allowlist + expiry (only after a connection is chosen). */}
      {connectionId ? (
        <>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="connect-grant-allowlist">
              <Trans>Tabelle consentite (opzionale)</Trans>
            </FieldLabel>
            <TokenInput
              id="connect-grant-allowlist"
              value={allowlist}
              onChange={setAllowlist}
              placeholder={i18n._(msg`es. invoices, customers`)}
              ariaLabel={i18n._(msg`Tabelle consentite`)}
              removeLabel={(t) => i18n._(msg`Rimuovi la tabella ${t}`)}
            />
            <p className="text-[11px] text-ink-2">
              <Trans>
                Lascia vuoto per consentire l'intera connessione in sola lettura, oppure aggiungi le
                tabelle una alla volta (Invio o virgola per confermare).
              </Trans>
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>
              <Trans>Scadenza (opzionale)</Trans>
            </FieldLabel>
            <DateTimePicker
              value={expiry}
              onChange={setExpiry}
              withTime
              minDate={new Date()}
              placeholder={i18n._(msg`Senza scadenza`)}
              ariaLabel={i18n._(msg`Scadenza della condivisione`)}
            />
          </div>
        </>
      ) : null}

      <FormFooter onBack={onBack} onSubmit={handleSubmit} pending={pending} disabled={!valid} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DOCUMENT form
// ---------------------------------------------------------------------------

function DocumentForm({
  onBack,
  onSubmit,
  pending,
}: {
  readonly onBack: () => void;
  readonly onSubmit: (g: ConnectGrantRequest) => void;
  readonly pending: boolean;
}) {
  const { i18n } = useLingui();
  const [docId, setDocId] = React.useState<string | null>(null);
  const [expiry, setExpiry] = React.useState<Date | null>(null);
  const { documents, isLoading } = useDocuments({ limit: 100 });

  const handleSubmit = () => {
    if (!docId) return;
    const config: Record<string, unknown> = {};
    if (expiry) config.expires_at = expiry.toISOString();
    onSubmit({
      grant_type: "document",
      content_item_id: docId,
      config: Object.keys(config).length > 0 ? config : null,
    });
  };

  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex flex-col gap-1.5">
        <FieldLabel required>
          <Trans>Documento</Trans>
        </FieldLabel>
        <DocumentPicker
          loading={isLoading}
          documents={documents}
          selected={docId ? [docId] : []}
          multi={false}
          onToggle={(id) => setDocId((cur) => (cur === id ? null : id))}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <FieldLabel>
          <Trans>Scadenza (opzionale)</Trans>
        </FieldLabel>
        <DateTimePicker
          value={expiry}
          onChange={setExpiry}
          withTime
          minDate={new Date()}
          placeholder={i18n._(msg`Senza scadenza`)}
          ariaLabel={i18n._(msg`Scadenza della condivisione`)}
        />
      </div>

      <FormFooter
        onBack={onBack}
        onSubmit={handleSubmit}
        pending={pending}
        disabled={docId === null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PickList — generic searchable single/multi selector (DRY across forms).
// ---------------------------------------------------------------------------

interface PickItem {
  readonly id: string;
  readonly label: string;
  readonly secondary?: string;
}

function PickList({
  items,
  selected,
  multi,
  onToggle,
  loading,
  emptyLabel,
}: {
  readonly items: readonly PickItem[];
  readonly selected: readonly string[];
  readonly multi: boolean;
  readonly onToggle: (id: string) => void;
  readonly loading: boolean;
  readonly emptyLabel: string;
}) {
  const { i18n } = useLingui();
  const [query, setQuery] = React.useState("");
  const filtered = query
    ? items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()))
    : items;
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  return (
    <div className="rounded-lg border border-black/10 bg-white">
      <div className="flex items-center gap-1.5 border-black/5 border-b px-2.5 py-1.5">
        <Search size={13} strokeWidth={2} className="shrink-0 text-ink-3" aria-hidden />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={i18n._(msg`Cerca…`)}
          aria-label={i18n._(msg`Cerca`)}
          className="min-w-0 flex-1 bg-transparent text-[12px] text-ink-1 outline-none placeholder:text-ink-3"
        />
      </div>
      <div
        role="listbox"
        aria-multiselectable={multi || undefined}
        aria-label={multi ? i18n._(msg`Selezione multipla`) : i18n._(msg`Selezione`)}
        className="max-h-[180px] overflow-y-auto py-1"
      >
        {loading ? (
          <p className="px-3 py-2 text-[11.5px] text-ink-2">
            <Trans>Carico…</Trans>
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-2 text-[11.5px] text-ink-2">{emptyLabel}</p>
        ) : (
          filtered.map((item) => {
            const isSel = selectedSet.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => onToggle(item.id)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors",
                  isSel
                    ? "bg-accent/[0.08] text-ink-1"
                    : "text-ink-1 hover:bg-cream-deep focus-visible:bg-cream-deep",
                  "focus-visible:outline-none",
                )}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium text-[12px]">{item.label}</span>
                  {item.secondary ? (
                    <span className="truncate text-[10.5px] text-ink-2">{item.secondary}</span>
                  ) : null}
                </span>
                <Check
                  size={14}
                  strokeWidth={2.5}
                  className={cn("shrink-0 text-accent", isSel ? "opacity-100" : "opacity-0")}
                  aria-hidden
                />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function DocumentPicker({
  documents,
  selected,
  multi,
  onToggle,
  loading,
}: {
  readonly documents: ReturnType<typeof useDocuments>["documents"];
  readonly selected: readonly string[];
  readonly multi: boolean;
  readonly onToggle: (id: string) => void;
  readonly loading: boolean;
}) {
  const { i18n } = useLingui();
  return (
    <PickList
      loading={loading}
      emptyLabel={i18n._(msg`Nessun documento disponibile`)}
      items={documents.map((d) => ({
        id: d.doc_id,
        label: d.title || d.filename,
        secondary: d.mime_type ?? undefined,
      }))}
      selected={selected}
      multi={multi}
      onToggle={onToggle}
    />
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function grantLabel(grant: ConnectGrant, i18n: ReturnType<typeof useLingui>["i18n"]): string {
  switch (grant.grant_type) {
    case "knowledge":
      if (grant.canvas_id) return i18n._(msg`Base di conoscenza`);
      return grant.content_item_ids && grant.content_item_ids.length > 0
        ? i18n._(msg`${grant.content_item_ids.length} documenti`)
        : i18n._(msg`Base di conoscenza`);
    case "connector": {
      const tables = grant.table_allowlist ?? [];
      return tables.length > 0
        ? i18n._(msg`Fonte connessa · ${tables.join(", ")}`)
        : i18n._(msg`Fonte connessa`);
    }
    case "document":
      return i18n._(msg`Documento`);
    default:
      return grant.grant_type;
  }
}

/**
 * Build an add-grant request from an existing grant (used by UNDO).
 *
 * Connector grants are CONNECTION-BASED: the table allowlist rides in
 * ``config.table_allowlist`` and ``read_only`` is forced by the BE, so we
 * fold any top-level ``table_allowlist`` the BE echoed back into ``config`` to
 * replay the exact scope. No ``capabilities`` — the concept is retired.
 */
function grantToRequest(grant: ConnectGrant): ConnectGrantRequest {
  let config = grant.config ?? undefined;
  if (
    grant.grant_type === "connector" &&
    grant.table_allowlist &&
    grant.table_allowlist.length > 0
  ) {
    config = { ...(config ?? {}), table_allowlist: [...grant.table_allowlist] };
  }
  return {
    grant_type: grant.grant_type,
    canvas_id: grant.canvas_id ?? undefined,
    content_item_ids: grant.content_item_ids ?? undefined,
    connection_id: grant.connection_id ?? undefined,
    content_item_id: grant.content_item_id ?? undefined,
    config,
  };
}
